import type { InsurancePropertyCategory } from "@prisma/client";
import { cn } from "../../lib/utils";

interface CategoryBadgeProps {
  category: InsurancePropertyCategory | string | null | undefined;
  className?: string;
  fullName?: boolean;
}

const CATEGORY_LABELS: Record<InsurancePropertyCategory, string> = {
  RESIDENTIAL: "Residential",
  STANDARD_RESIDENTIAL: "Standard Residential",
  HIGH_END_RESIDENTIAL: "High-End Residential",
  STANDARD_COMMERCIAL: "Standard Commercial",
  MIXED_USE: "Mixed Use",
};

const CATEGORY_LETTERS: Record<InsurancePropertyCategory, string> = {
  RESIDENTIAL: "R",
  STANDARD_RESIDENTIAL: "SR",
  HIGH_END_RESIDENTIAL: "HR",
  STANDARD_COMMERCIAL: "SC",
  MIXED_USE: "MU",
};

const CATEGORY_STYLES: Record<InsurancePropertyCategory, string> = {
  RESIDENTIAL: "bg-emerald-100 text-emerald-700",
  STANDARD_RESIDENTIAL: "bg-sky-100 text-sky-700",
  HIGH_END_RESIDENTIAL: "bg-amber-100 text-amber-700",
  STANDARD_COMMERCIAL: "bg-slate-100 text-slate-700",
  MIXED_USE: "bg-teal-100 text-teal-700",
};

function isKnownCategory(value: unknown): value is InsurancePropertyCategory {
  return typeof value === "string" && value in CATEGORY_LABELS;
}

export const CATEGORY_OPTIONS: { value: InsurancePropertyCategory; label: string }[] = (
  Object.keys(CATEGORY_LABELS) as InsurancePropertyCategory[]
).map((value) => ({ value, label: CATEGORY_LABELS[value] }));

export function CategoryBadge({ category, className, fullName }: CategoryBadgeProps): React.ReactElement | null {
  if (!isKnownCategory(category)) return null;

  return (
    <span
      title={CATEGORY_LABELS[category]}
      data-testid="category-badge"
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
        CATEGORY_STYLES[category],
        className,
      )}
    >
      {fullName ? CATEGORY_LABELS[category] : CATEGORY_LETTERS[category]}
    </span>
  );
}
